import {
  Navigate,
  Outlet,
  useMatch,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';

import { HOME_ROUTE_PATTERN, PROPERTY_PATH, homePath } from './paths.js';
import { findAccountHome } from './zoneValidation.js';
import PropertySkeleton from '../screens/PropertySkeleton.jsx';

// Layout route for /property/:homeId — a home's Property page (the index) and its
// nested property-level action screens (holiday / data). Same contract as
// ZoneRoute: validate the home against the seeded list, wait for the hello before
// deciding, and hand the validated homeId to the screens via outlet context.
export default function HomeRoute() {
  const { homeId } = useParams();
  const homes = useSelector((state) => state.homeList.homes);
  const hadFirstHello = useSelector((state) => state.homeList.hadFirstHello);
  // A home with no cloud licence has nothing for these screens to show or send,
  // and the app never offers them: its Property screen is replaced outright, so a
  // zone or a holiday can't be reached from it. The web can be deep-linked to any
  // of them, so they bounce to the Property page, which carries the notice (and
  // the menu and home selector with it). The index must NOT bounce, or it would
  // loop — hence the pattern match rather than a blanket redirect.
  const cloudSupported = useSelector((state) =>
    selectors.isCloudSupported(state, homeId),
  );
  const atPropertyIndex = useMatch(HOME_ROUTE_PATTERN) !== null;

  // A deep link / refresh lands here before the hello; hold the Property
  // skeleton (this route's index is the Property screen) rather than blank text.
  if (!hadFirstHello) {
    return <PropertySkeleton message="Loading your properties…" />;
  }

  const home = findAccountHome(homes, homeId);
  if (!home) {
    return <Navigate to={PROPERTY_PATH} replace />;
  }

  if (!cloudSupported && !atPropertyIndex) {
    return <Navigate to={homePath(homeId)} replace />;
  }

  return <Outlet context={{ homeId }} />;
}

// Typed accessor for the validated home context, used by the property-level
// action screens.
export function useHomeRouteContext() {
  return useOutletContext();
}
